import { Fragment, useEffect, useRef } from 'react';

import { rem } from '@blueprint/ui';

import { ScrollContainer } from 'components/ScrollContainer/ScrollContainer';
import { PromoteInputFeedback } from 'components/Reply/PromoteInputFeedback/PromoteInputFeedback';
import {
  isAgentReply,
  isChatbotReply,
  isPromoteInputFeedbackReply,
} from 'components/Reply/reply-type';
import { ChatbotReply } from 'components/Reply/ChatbotReply';
import { AgentReply } from 'components/Reply/AgentReply';
import { oneColumnLayout } from 'themes';

import { Question } from './Question';
import { CHAT_UNAVAILABLE_ENTRY } from '../SupportPanel';

/**
 * QuestionAnswerPanel
 * Displaying Q&As
 */
export const ConversationPanel = ({
  conversation,
  onAnswerFaqLinkClick,
  onFeedback,
  display,
  onSkipFeedback,
}) => {
  const latestQuestionRef = useRef(null);
  const latestAgentReplyRef = useRef(null);

  const latestQuestionIndex = conversation.length - 1;

  useEffect(() => {
    if (!display) {
      return;
    }

    if (latestAgentReplyRef.current) {
      latestAgentReplyRef.current.scrollIntoView();

      return;
    }

    latestQuestionRef.current?.scrollIntoView();
  }, [conversation, display]);

  return (
    <ScrollContainer
      as="ol"
      sx={{
        flexGrow: 1,
        flexDirection: 'column',
        [oneColumnLayout]: {
          maxHeight: rem(300),
        },
        margin: 0,
        paddingLeft: 0,
      }}
      aria-live="polite"
    >
      {conversation.map(
        (
          {
            answer,
            question,
            id,
            datetime,
            connectors,
            answerID,
            dtreenodeid,
            dtreeobjectid,
            displayName,
            replyType,
            coBrowse,
            content,
          },
          idx,
        ) => {
          const isLatestConversation = idx === latestQuestionIndex;

          return (
            <Fragment key={id}>
              {question && question !== CHAT_UNAVAILABLE_ENTRY && (
                <Question
                  ref={isLatestConversation ? latestQuestionRef : null}
                  content={question}
                  datetime={datetime}
                />
              )}
              {isPromoteInputFeedbackReply(replyType) && (
                <PromoteInputFeedback
                  onSkipFeedback={onSkipFeedback}
                  datetime={datetime}
                  isFeedbackEnable={isLatestConversation}
                />
              )}
              {answer && isChatbotReply(replyType) && (
                <ChatbotReply
                  answer={{
                    content: answer,
                    datetime,
                    id: answerID ?? id,
                    displayName,
                  }}
                  ref={
                    isLatestConversation && question === CHAT_UNAVAILABLE_ENTRY
                      ? latestQuestionRef
                      : null
                  }
                  dtree={{
                    nodeID: dtreenodeid,
                    objectID: dtreeobjectid,
                  }}
                  connectors={connectors?.Connectors}
                  isFeedbackEnable={isLatestConversation}
                  onFaqLinkClick={onAnswerFaqLinkClick}
                  onClickFeedback={onFeedback}
                />
              )}
              {answer && isAgentReply(replyType) && (
                <AgentReply
                  name={displayName}
                  datetime={datetime}
                  coBrowse={coBrowse}
                  content={content}
                  isFeedbackEnable={isLatestConversation}
                  ref={isLatestConversation ? latestAgentReplyRef : null}
                  answer={answer}
                />
              )}
            </Fragment>
          );
        },
      )}
    </ScrollContainer>
  );
};
